import React, { useState, useEffect } from 'react'
import { useLazyQuery } from '@apollo/client'
import { GET_COUNTRY_LIST, GET_LOCATION_LIST } from 'graphql/common/queries'
import { Input, InputGroup } from 'components/input'
import {
    MagnifyingGlassIcon,
    EllipsisVerticalIcon,
    ArrowLeftIcon,
    UsersIcon,
    PencilIcon,
    EyeIcon,
} from '@heroicons/react/16/solid'
import { t } from 'i18next'
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from 'components/dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Divider } from 'components/divider'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import loadingImage from '../../assets/images/boat-loading.gif'
import { ErrorDialog, SuccessDialog, ConfirmDialog } from 'components/custom/ResponseDialog'
import { useNavigate } from 'react-router-dom'
import { getPageRange } from 'utils/paginationHelper'

export default function Locations() {
    const navigate = useNavigate()
    // Location data
    const [searchString, setSearchString] = useState('')
    const [isSearch, setIsSearch] = useState(false)
    const [countries, setCountries] = useState([])

    // Dialog Message

    const [locationList, setLocationList] = useState(null)
    const [locationCount, setLocationCount] = useState(0)
    const [searchLocationStatus, setSearchLocationStatus] = useState('ACT')
    console.log(locationList)
    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(locationCount / rowsPerPage)

    const [getLocationList, { loading: locationListLoading, error: locationListError }] = useLazyQuery(
        GET_LOCATION_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchLocation.success && data.searchLocation.code === '200') {
                    setLocationList(data.searchLocation.locations)
                    setLocationCount(data.searchLocation.total_count)
                }
            },
        }
    )

    const [getCountryList, { loading: countryListLoading }] = useLazyQuery(GET_COUNTRY_LIST, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.country) {
                setCountries(data.country)
            }
        },
    })

    // Search locations Location List
    const searchLocations = (searchKey, status) => {
        getLocationList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                searchString: searchKey === '' ? null : searchKey,
                searchLocationId: null,
            },
        })
    }

    useEffect(() => {
        getCountryList({
            variables: {
                code: null,
            },
        })
    }, [])

    const getCountryFlag = countryCode => {
        console.log(countryCode)
        if (!countryCode) return null
        return `https://flagcdn.com/w160/${countryCode.toLowerCase()}.png`
    }

    useEffect(() => {
        searchLocations(searchString, searchLocationStatus)
    }, [page, rowsPerPage])

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const onClickSearch = () => {
        console.log('searchString', searchString)
        setPage(0)
        setRowsPerPage(10)
        searchLocations(searchString, searchLocationStatus) //
    }
    const onClickSearchClear = () => {
        console.log('searchString before clear:', searchString)
        setSearchString('') // Clear the search string
        setSearchLocationStatus('All')
        setPage(0)
        setRowsPerPage(10)
        searchLocations('', 'All') // Call searchLocations with an empty string after clearing
        // Let state update before invoking
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div>
                        <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between">
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                                        <UsersIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                                    </div>
                                    <div>
                                        <span className="ms-3"> {t('Manage Locations')}</span>
                                        <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                            {t('Easily view, add, and edit trip locations.')}
                                        </p>
                                    </div>
                                </h1>
                            </div>
                            <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap sm:text-end">
                                <button
                                    onClick={() => navigate('/location/location-form')}
                                    type="button"
                                    className="hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                >
                                    {t('Add Location')}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap max-w-xl gap-4">
                        <div className="flex-2">
                            <InputGroup>
                                <MagnifyingGlassIcon />
                                <Input
                                    value={searchString}
                                    onChange={e => setSearchString(e.target.value)}
                                    name="search"
                                    id="search"
                                    placeholder={t('Search locations')}
                                />
                            </InputGroup>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={onClickSearch}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Search')}
                            </button>
                            <button
                                onClick={onClickSearchClear}
                                type="button"
                                className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Divider />

            {locationListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {locationListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch locations!')}</div>
                </div>
            )}

            {!locationListLoading && !locationListError && locationList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!locationListLoading && !locationListError && locationList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('English Name')}</TableHeader>
                                    <TableHeader>{t('Arabic Name')}</TableHeader>
                                    <TableHeader>{t('Country')}</TableHeader>
                                    <TableHeader>{t('Area')}</TableHeader>
                                    <TableHeader>{t('latitude')}</TableHeader>
                                    <TableHeader>{t('longitude')}</TableHeader>
                                    <TableHeader>{t('Actions')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locationList.map(location => (
                                    <TableRow
                                        key={location.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => navigate(`/location/location-details/${location.id}`)}
                                        title={`Location #${location.id}`}
                                    >
                                        <TableCell>{location.name.en}</TableCell>
                                        <TableCell>{location.name.ar}</TableCell>
                                        <TableCell>
                                            {location?.address?.country_code && (
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={getCountryFlag(location?.country_details?.country_code)}
                                                        alt={location?.country_details?.country_code}
                                                        className="w-5 h-3"
                                                        onError={e => {
                                                            e.target.style.display = 'none'
                                                        }}
                                                    />
                                                    <span>{location.country_details?.country}</span>
                                                    <span>{`(${location.country_details?.country_ar})`}</span>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>{location.address?.area}</TableCell>
                                        <TableCell>{location.geo.lat}</TableCell>
                                        <TableCell>{location.geo.lng}</TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/location/location-details/${location.id}`)
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                    <DropdownDivider />
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/location/location-form/${location.id}`)
                                                        }}
                                                    >
                                                        <PencilIcon />
                                                        <DropdownLabel>{t('Edit')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
        </>
    )
}
